import { Fragment, type ReactNode } from "react";
import {
  formatWhaleChatReply,
  resolveAttachmentPlaceholders,
  type WhaleMissionAttachment,
} from "@vida/wave-core";

export type WhaleChatMessageAttachment = {
  id: string;
  url: string;
  mime?: string;
};

function isSafeImageUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

function toMissionAttachments(
  attachments?: WhaleChatMessageAttachment[],
): WhaleMissionAttachment[] {
  if (!attachments?.length) return [];
  return attachments.map((att) => ({
    id: att.id,
    mime: att.mime,
    presignedUrl: att.url,
  }));
}

function renderInlineContent(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)|(\*\*[^*]+\*\*)/g;
  let lastIndex = 0;
  let partIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(
        <Fragment key={`${keyPrefix}-t-${partIndex}`}>
          {text.slice(lastIndex, match.index)}
        </Fragment>,
      );
      partIndex += 1;
    }

    if (match[2]) {
      const url = match[2];
      if (isSafeImageUrl(url)) {
        nodes.push(
          <img
            key={`${keyPrefix}-img-${partIndex}`}
            src={url}
            alt={match[1]?.trim() || "Allegato generato"}
            className="wave-attachment-image"
            style={{ maxWidth: "100%", marginTop: "0.5rem", borderRadius: 8 }}
            loading="lazy"
          />,
        );
      }
      partIndex += 1;
    } else if (match[3]) {
      nodes.push(
        <strong key={`${keyPrefix}-b-${partIndex}`}>{match[3].slice(2, -2)}</strong>,
      );
      partIndex += 1;
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(
      <Fragment key={`${keyPrefix}-t-${partIndex}`}>{text.slice(lastIndex)}</Fragment>,
    );
  }

  return nodes;
}

function renderBasicMarkdown(text: string): ReactNode {
  const lines = text.split(/\n/);
  const nodes: ReactNode[] = [];
  let listItems: string[] = [];
  let nodeIndex = 0;

  const flushList = () => {
    if (!listItems.length) return;
    nodes.push(
      <ul key={`list-${nodeIndex}`} className="wave-whale-chat-list">
        {listItems.map((item, i) => (
          <li key={`li-${nodeIndex}-${i}`}>{renderInlineContent(item, `li-${nodeIndex}-${i}`)}</li>
        ))}
      </ul>,
    );
    nodeIndex += 1;
    listItems = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    const imageLine = trimmed.match(/^!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)\s*$/);
    if (imageLine && isSafeImageUrl(imageLine[2])) {
      flushList();
      nodes.push(
        <p key={`img-${nodeIndex}`} className="wave-whale-chat-paragraph wave-whale-chat-paragraph--image">
          <img
            src={imageLine[2]}
            alt={imageLine[1]?.trim() || "Allegato generato"}
            className="wave-attachment-image"
            style={{ maxWidth: "100%", marginTop: "0.5rem", borderRadius: 8 }}
            loading="lazy"
          />
        </p>,
      );
      nodeIndex += 1;
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      listItems.push(trimmed.replace(/^[-*]\s+/, ""));
      continue;
    }

    flushList();
    if (!trimmed) continue;
    nodes.push(
      <p key={`p-${nodeIndex}`} className="wave-whale-chat-paragraph">
        {renderInlineContent(trimmed, `p-${nodeIndex}`)}
      </p>,
    );
    nodeIndex += 1;
  }

  flushList();
  return nodes;
}

type Props = {
  text: string;
  /** When true, skip orchestrator cleanup (already sanitized). */
  raw?: boolean;
  /** Live or persisted attachments used to resolve `[attachment:<uuid>]` markers at render-time. */
  attachments?: WhaleChatMessageAttachment[];
};

export function WhaleChatMessageBody({ text, raw = false, attachments }: Props) {
  const cleaned = (raw ? text : formatWhaleChatReply(text)).trim();
  if (!cleaned) return null;

  const missionAttachments = toMissionAttachments(attachments);
  const body =
    missionAttachments.length > 0
      ? resolveAttachmentPlaceholders(cleaned, missionAttachments)
      : cleaned;

  if (!body) return null;
  return <div className="wave-whale-chat-markdown">{renderBasicMarkdown(body)}</div>;
}
